"use client";

import { useCallback } from "react";
import type {
  SelectedProfileInput,
  SelectedProfileOutput,
  Target,
  Version,
} from "@/generated/fastapi";
import {
  useThoughtArrangementPreferences,
  useUserChoices,
  useUserProfile,
} from "@/state/globalStore";
import { usePrintSettingsActions } from "@/features/profile";
import { useTargetsQuery } from "@/features/targets";
import { useSituationsQuery } from "@/features/situations";

function getArrangementTargetKey(target?: Target | null) {
  return target?.id ?? target?.content ?? "no-rerank-target";
}

function applyManualArrangementOverrides({
  selectedProfile,
  target,
  setThoughtArrangementManualOverride,
}: {
  selectedProfile: SelectedProfileOutput;
  target?: Target | null;
  setThoughtArrangementManualOverride: (
    scopeKey: string,
    value: boolean,
  ) => void;
}) {
  if (!selectedProfile.manual_rerank) {
    return;
  }

  const targetKey = getArrangementTargetKey(target);

  (selectedProfile.selected_thoughts ?? []).forEach((selection) => {
    setThoughtArrangementManualOverride(
      [selection.prototype, selection.situation_id ?? null, targetKey].join(":"),
      true,
    );
  });

  (selectedProfile.selected_thoughts_free ?? []).forEach((selection) => {
    setThoughtArrangementManualOverride(
      ["free-list", selection.prototype, targetKey].join(":"),
      true,
    );
  });
}

export default function useApplyVersionSelection() {
  const { userProfile } = useUserProfile();
  const {
    applySelectedProfileSelection,
    resetSelectedProfile,
    setActiveStudioExportLock,
    setActiveStudioContent,
    setActiveStudioGrouping,
    setActivePrototypeCategory,
    setInterimState,
    setTargetsToRerank,
    setActiveProfileVersion,
  } = useUserChoices();
  const {
    resetThoughtArrangementManualOverrides,
    setThoughtArrangementManualOverride,
  } = useThoughtArrangementPreferences();
  const { updatePrintSettingsSync } = usePrintSettingsActions();
  const targetsQuery = useTargetsQuery();
  const situationsQuery = useSituationsQuery();

  const isReadyToApply =
    !targetsQuery.isLoading &&
    !situationsQuery.isLoading &&
    !targetsQuery.isError &&
    !situationsQuery.isError;

  const applyVersionSelection = useCallback(
    (version: Version) => {
      const selectedProfile = version.selected_profile;
      if (!selectedProfile) {
        return;
      }
      const confirmedSelectedProfile: SelectedProfileInput = {
        ...selectedProfile,
        selected_suggested_thoughts: undefined,
        active_studio_content:
          selectedProfile.active_studio_content === "suggested"
            ? "confirmed"
            : selectedProfile.active_studio_content,
      };

      resetSelectedProfile();
      resetThoughtArrangementManualOverrides();

      applySelectedProfileSelection({
        selectedProfile: confirmedSelectedProfile,
        existingSituationIds: (situationsQuery.data ?? []).map(
          (situation) => situation.id,
        ),
      });

      const currentPrintLanguage = userProfile.data.print_settings.print_language;
      const versionLanguage = version.language || currentPrintLanguage;
      if (versionLanguage !== currentPrintLanguage) {
        void updatePrintSettingsSync({ print_language: versionLanguage });
      }

      setActiveProfileVersion({
        id: version.id,
        name: version.name,
        target_id: version.target_id ?? null,
        language: version.language ?? null,
        latest_at: version.latest_at ?? null,
      });
      setInterimState("profile_shaping_guidance", {
        isOpen: true,
        value: "loaded_selection_search",
      });

      const restoredContent =
        confirmedSelectedProfile.active_studio_content ?? "confirmed";
      setActiveStudioContent(restoredContent);

      if (selectedProfile.active_studio_grouping) {
        setActiveStudioGrouping(selectedProfile.active_studio_grouping);
      }

      if (selectedProfile.active_prototype_category) {
        setActivePrototypeCategory(selectedProfile.active_prototype_category);
      }

      setActiveStudioExportLock(restoredContent === "confirmed");

      const targetToSet = version.target_id
        ? (targetsQuery.data ?? []).find(
            (target) => target.id === version.target_id,
          )
        : null;

      applyManualArrangementOverrides({
        selectedProfile,
        target: targetToSet,
        setThoughtArrangementManualOverride,
      });

      if (!version.target_id) {
        setTargetsToRerank([]);
        return;
      }

      if (targetToSet) {
        setTargetsToRerank([targetToSet]);
        return;
      }

      setTargetsToRerank([]);
    },
    [
      applySelectedProfileSelection,
      resetSelectedProfile,
      resetThoughtArrangementManualOverrides,
      setActivePrototypeCategory,
      setActiveStudioContent,
      setActiveStudioExportLock,
      setActiveStudioGrouping,
      setInterimState,
      setActiveProfileVersion,
      setTargetsToRerank,
      setThoughtArrangementManualOverride,
      situationsQuery.data,
      targetsQuery.data,
      updatePrintSettingsSync,
      userProfile.data.print_settings.print_language,
    ],
  );

  return {
    applyVersionSelection,
    isReadyToApply,
    targetsQuery,
    situationsQuery,
  };
}
